<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import UIKit, { type ITableSortSettings, type ITableColumn, type TTableRow } from '@pocketprep/ui-kit'

type TStudent = {
    id: string
    student: string
    examDate: string
    startDate: string
    sortName: string
    styles?: { [key: string]: string }
    isPending: boolean
}

type TStudentStatusOptions = {
    id: string
    label: string
    enabled: boolean
}

const {
    SidePanel,
    Table,
    Button: PocketButton,
    Search,
    TablePagination,
    Checkbox,
    OverflowTooltip,
    FilterDropdown,
    Pill,
    EmptyState,
    Tag,
} = UIKit

const props = defineProps<{
    tableTitle: string
    studentOptions: TStudent[]
    initialSelectedStudentIds: string[]
}>()

const emit = defineEmits<{
    updateSelectedStudents: [selectedStudentIds: string[]]
    close: [value?: boolean]
}>()

const searchString = ref<string | null>(null)
const perPage = ref(10)
const currentPage = ref(0)
const selectedStudentIds = ref<string[]>([])
const existingStudentIds = ref<string[]>([])
const rowHoverId = ref<string | null>(null)
const studentColumns: ITableColumn[] = [{
    name: 'Student',
    propName: 'student',
    styles: {
        'width': '197px',
        'margin-right': '10px',
    },
}, {
    name: 'Exam Date',
    propName: 'examDate',
    styles: {
        'display': 'block',
        'width': '89px',
    },
}, {
    name: 'Start Date',
    propName: 'startDate',
    styles: {
        'display': 'block',
        'width': '89px',
    },
}, {
    propName: 'status',
    isLabelHidden: true,
    styles: {
        width: '58px',
    },
}]
const checkboxOptions = ref<TStudentStatusOptions[]>([
    {
        id: 'active',
        label: 'Active',
        enabled: false,
    },
    {
        id: 'pending',
        label: 'Pending',
        enabled: false,
    },
])
const areAllStudentsSelected = ref(false)
const sortSettings = ref<ITableSortSettings>({ column: studentColumns[0], direction: 1 })

const enabledFilterStatuses = computed(() => {
    return checkboxOptions.value.reduce<string[]>((acc, s) => {
        if (s.enabled) {
            const first = s.label.split(' ')[0]
            if (first !== undefined) {
                acc.push(first)
            }
        }
        return acc
    }, [])
})

const mappedStudents = computed(() => {
    const sortSettingsVal = sortSettings.value
    return (JSON.parse(JSON.stringify(props.studentOptions)) as TStudent[])
        .map(s => {
            s.styles = {
                userSelect: 'none',
                cursor: 'pointer',
            }

            if (s.id === rowHoverId.value) {
                s.styles.backgroundColor = `${BrandColors.grayBackground}60`
            }

            return s
        })
        .filter(s => {
            const searchStringVal = searchString.value
            const studentStatus = s.isPending ? 'Pending' : 'Active'
            const isExistingInGroup = existingStudentIds.value.includes(s.id)
            // Matches anything searched without filters
            const matchesSearch = !searchStringVal || s.student.toLowerCase().includes(searchStringVal.toLowerCase())
            // Matches anything filtered
            const matchesFilter = enabledFilterStatuses.value.includes(studentStatus)
                || !enabledFilterStatuses.value.length
            return matchesSearch && matchesFilter && !isExistingInGroup
        })
        .sort((a, b) => {
            if (sortSettingsVal?.column) {
                if (sortSettingsVal.column.name === 'Student') {
                    return sortSettingsVal.direction * a.sortName.localeCompare(b.sortName)
                }
                if (sortSettingsVal.column.name === 'Start Date') {
                    const aStartDate = new Date(a.startDate).getTime() || 0
                    const bStartDate = new Date(b.startDate).getTime() || 0
                    return sortSettingsVal.direction * (bStartDate - aStartDate)
                }
                if (sortSettingsVal.column.name === 'Exam Date') {
                    const aExamDate = new Date(a.examDate).getTime() || 0
                    const bExamDate = new Date(b.examDate).getTime() || 0
                    return sortSettingsVal.direction * (bExamDate - aExamDate)
                }
            }

            return -1
        })
})

const activeCountForGroup = computed(() => {
    return mappedStudents.value.filter(ms => ms && !ms.isPending).length
})

const pendingCountForGroup = computed(() => {
    return mappedStudents.value.filter(ms => ms?.isPending).length
})

const enabledFilters = computed(() => {
    return enabledFilterStatuses.value.map(efs => ({
        id: efs.toLocaleLowerCase(),
        label: efs,
        showClose: true,
    }))
})

const removeFilter = (filterId: string) => {
    checkboxOptions.value = checkboxOptions.value.map(o => o.id === filterId ? { ...o, enabled: false } : o)
}

const isFilterEnabled = (status: string) => {
    return checkboxOptions.value.some(s => s.id === status && s.enabled === true)
}

const setFilterOptions = () => {
    checkboxOptions.value = [
        {
            id: 'active',
            label: `Active (${activeCountForGroup.value})`,
            enabled: isFilterEnabled('active'),
        },
        {
            id: 'pending',
            label: `Pending (${pendingCountForGroup.value})`,
            enabled: isFilterEnabled('pending'),
        },
    ]
}

const allStudentsSelected = computed(() => {
    // Are all students selected? If yes, we return true (but we want to return false if
    // they aren't and null if we are intermediate state)
    return (selectedStudentIds.value.length && mappedStudents.value.length
        && selectedStudentIds.value.length === mappedStudents.value.length)
        ? true
        : selectedStudentIds.value.length === 0 && !areAllStudentsSelected.value ? false : null
})

const selectAllStudents = () => {
    // Either all already selected or intermediate state (modelValue is null)
    if (allStudentsSelected.value !== false) {
        selectedStudentIds.value = []
    } else {
        selectedStudentIds.value = [ ...mappedStudents.value.map(mss => mss.id) ]
    }
}

const emptyStateTitle = computed(() => {
    return searchString.value ? `No results for ${searchString.value}` : 'No results'
})

const emptyStateMessage = computed(() => {
    return 'There are no results that match your filter and search criteria.'
})

const pagedStudents = computed(() => {
    const perPageVal = perPage.value
    return mappedStudents.value.slice(currentPage.value * perPageVal, (currentPage.value * perPageVal) + perPageVal)
})

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const rowClicked = (row: { id: string }) => {
    if (selectedStudentIds.value.includes(row.id)) {
        selectedStudentIds.value = selectedStudentIds.value.filter(id => id !== row.id)
    } else {
        selectedStudentIds.value.push(row.id)
    }
    return selectedStudentIds.value
}

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const submitSelectStudents = () => {
    emit('updateSelectedStudents', selectedStudentIds.value)
}

const emitClose = () => {
    emit('close', true)
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = String(row.id)
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

watch(selectedStudentIds, setFilterOptions)

watch(() => pagedStudents.value.length, () => {
    const perPageVal = perPage.value
    if (mappedStudents.value.length < perPageVal) {
        currentPage.value = 0
    }
})

onMounted(() => {
    existingStudentIds.value = props.initialSelectedStudentIds
    setFilterOptions()
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            name="Add Students"
            class="select-students"
            :isTeachSidePanel="true"
            @close="emitClose"
        >
            <div class="select-students__title" v-html="tableTitle" />
            <div class="select-students__table-head">
                <Search
                    v-model="searchString"
                    class="select-students__table-search"
                    placeholder="Search"
                    type="white"
                    @input="setFilterOptions"
                />
                <FilterDropdown
                    v-model="checkboxOptions"
                    class="select-students__filter"
                    filter-options-label="FILTER BY STATUS"
                    filterLabel=""
                    :radioEntries="[]"
                    :checkboxEntries="checkboxOptions"
                />
                <TablePagination
                    :per-page="perPage"
                    unit="students"
                    :current-page="currentPage"
                    :total="mappedStudents.length"
                    @changePage="changePage($event)"
                />
            </div>
            <div
                v-if="enabledFilters.length"
                class="select-students__filters">
                <span class="select-students__filters-label">Filters:</span>
                <Pill
                    v-for="filter in enabledFilters"
                    :key="filter.id"
                    :show-close="true"
                    class="students-table__filter-chip"
                    @click="removeFilter(filter.id)"
                >
                    {{ filter.label }}
                </Pill>
            </div>
            <div class="select-students__bulk-actions">
                <div
                    class="select-students__bulk-actions-checkbox-container"
                >
                    <Checkbox
                        :modelValue="allStudentsSelected"
                        class="select-students__bulk-actions-checkbox"
                        @click="selectAllStudents"
                    />
                </div>
                <div
                    class="select-students__bulk-actions-num-checked"
                >
                    {{ selectedStudentIds.length }} of {{ mappedStudents.length }} selected
                </div>
            </div>
            <Table
                key="select-students__students"
                :rows="pagedStudents"
                :columns="studentColumns"
                :default-sort="sortSettings"
                :sort-toggle-order="[ 1, -1 ]"
                :show-header="false"
                class="select-students__table"
                :table-column-label-styles="{
                    margin: '0 auto',
                    paddingTop: '7px',
                    color: '#1F3545',
                    fontSize: '12px',
                }"
                @sort="updateSortSettings"
                @rowClicked="rowClicked({id: $event.id as string})"
                @rowEnter="rowEnter"
                @rowLeave="rowLeave"
            >
                <template #tableCellValue="{ row, column }">
                    <div
                        v-if="column.propName === 'student'"
                        class="select-students__checkbox-name"
                    >
                        <Checkbox :modelValue="selectedStudentIds.includes(row.id)" class="select-students__checkbox" />
                        <div class="select-students__name">
                            <OverflowTooltip>
                                {{ row.student }}
                            </OverflowTooltip>
                        </div>
                    </div>
                    <template v-if="column.propName === 'status'">
                        <Tag
                            v-if="row.isPending"
                            :isPendingTag="true"
                            size="small"
                        >
                            <template #tagMessage>
                                Pending
                            </template>
                        </Tag>
                        <Tag
                            v-else
                            :isActiveTag="true"
                            size="small"
                        >
                            <template #tagMessage>
                                Active
                            </template>
                        </Tag>
                    </template>
                </template>
                <template #tableRows>
                    <EmptyState
                        v-if="!pagedStudents.length"
                        class="select-students__empty-state"
                        :title="emptyStateTitle"
                        :message="emptyStateMessage"
                    >
                        <template #imageRow>
                            <div />
                        </template>
                    </EmptyState>
                </template>
            </Table>

            <template #action>
                <PocketButton
                    type="primary"
                    :disabled="selectedStudentIds.length === 0"
                    @click="submitSelectStudents"
                >
                    Add Students
                </PocketButton>
            </template>

            <template #details>
                <div class="select-students__details">
                    <span>{{ selectedStudentIds.length }}</span> Students Selected
                </div>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>


.select-students {
    :deep(*) {
        .uikit-side-panel__content {
            padding-top: 24px;
            width: 498px;
        }

        .uikit-table__column-labels {
            border-top: 1px solid $fog;
            height: 38px;
        }
        .uikit-table__content {
            padding-left: 11px;
        }
    }
    &__title {
        color: $brand-black;
        font-size: 15px;
        line-height: 20px;
        margin-bottom: 36px;
    }

    &__details {
        color: $slate-01;
        font-size: 13px;
        line-height: 18px;

        span {
            font-weight: 600;
            color: $slate-03;
        }
    }

    &__checkbox-name {
        display: flex;
        align-items: center;
    }

    &__checkbox {
        margin-right: 10px;
    }

    &__name {
        width: calc(100% - 28px);
    }

    &__table-head {
        display: flex;
        margin: 11px 11px 20px;
        justify-content: space-between;
        align-items: center;
    }

    &__table-search {
        width: 262px;
    }

    &__bulk-actions {
        display: flex;
        padding-left: 23px;
        margin-bottom: 11px;
    }

    &__bulk-actions-num-checked {
        font-size: 12px;
        line-height: 16px;
        margin: 2px 30px;
        margin-left: 23px;
    }

    &__filters {
        height: 30px;
        margin: 0 0 8px 3px;
    }

    &__filters-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate-03;
        margin-right: 9px;
        display: inline-block;
        margin-left: 12px;
        vertical-align: top;
    }

    &__empty-state {
        margin: 0 auto;
        padding-top: 40px;
    }
}
</style>
